import { Intent } from "@blueprintjs/core";
import { noop } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled, { css } from "styled-components";

import {
  Flex,
  HexButton,
  HexLinkButton,
  HexTag,
  HexTooltip,
} from "../../hex-components";
import { useCurrentUser } from "../../hooks/me/useCurrentUser";
import { LocalStorageKeys, useLocalStorage } from "../../hooks/useLocalStorage";
import { useToggleState } from "../../hooks/useToggleState";
import { Routes } from "../../route/routes";
import { ClientCounterEvent } from "../../util/clientCounterEvent";
import { TestSelectors, dataTest } from "../../util/testSelectors";
import { trackCounterEvent } from "../../util/trackEvent";
import { useHexFlag } from "../../util/useHexFlags";
import { useThreadsEnabledForUser } from "../agent/ask-from-home/hooks/useThreadsEnabledForUser";
import { HexLogo, HexLogoProps } from "../app/HexLogo";
import { NotificationInboxButton } from "../app/NotificationInbox";
import { useGetUnreadNotificationCountQuery } from "../app/NotificationInbox.generated";
import { UnreadIndicator } from "../app/UnreadIndicator";
import { ErrorBoundary } from "../common/ErrorBoundary";
import { ScrolledArea } from "../common/ScrolledArea";
import { WorkspaceSearchButton } from "../common/WorkspaceSearchButton";
import {
  CollectionIcon,
  ComponentIcon,
  ConfigureIcon,
  ContextStudioIcon,
  DataSourcesIcon,
  ExploreIcon,
  HomeIcon,
  LibraryIcon,
  MenuIcon,
  ProjectIcon,
  SidebarDockIcon,
  ThreadIcon,
} from "../icons/CustomIcons";
import {
  SIDEBAR_PADDING,
  SIDEBAR_SPACER,
} from "../sidebar/sidebarSharedStyles";

import {
  FLOATING_SIDEBAR_BOTTOM_SPACING,
  FLOATING_SIDEBAR_TOP_SPACING,
  NAVIGATION_BAR_HEIGHT,
  POPOVER_SIDEBAR_WIDTH,
} from "./constants";
import { CreateAssetDialogs } from "./CreateNewButtons";
import { FavoriteCollections } from "./FavoriteCollections";
import { FavoriteData } from "./FavoriteData";
import { FavoriteProjects } from "./FavoriteProjects";
import { QuickCreateButtons } from "./QuickCreateButtons";
import { RecentItems } from "./RecentItems";
import { useHasLibraryStatuses } from "./useHasLibraryStatuses";
import { WorkspaceDropdownPicker } from "./WorkspaceDropdownPicker";

enum PopoverSidebarState {
  DOCKED = "docked",
  HOVERED = "hovered",
  CLOSED = "closed",
}

const PopoverSidebarContainer = styled.div<{
  $variant: PopoverSidebarContextType["state"];
}>`
  width: ${POPOVER_SIDEBAR_WIDTH}px;
  background-color: transparent;

  ${({ $variant }) =>
    $variant === "docked"
      ? css`
          position: unset;
          height: 100%;
        `
      : css`
          position: absolute;
          top: ${NAVIGATION_BAR_HEIGHT}px;
          padding-top: 10px;
          max-height: calc(100% - ${NAVIGATION_BAR_HEIGHT}px);
          height: 100%;
          z-index: 20;
          left: 0;
          padding-left: 10px;
          transition:
            transform
              ${({ theme }) =>
                `${theme.animation.durationMedium} ${theme.animation.easing}`},
            opacity
              ${({ theme }) =>
                `${theme.animation.duration} ${theme.animation.easing}`};

          ${$variant === "hovered"
            ? `
              opacity: 1;
              transform: translateX(0);
              pointer-events: all;

            `
            : `
              transform: translateX(-30px);

              opacity: 0;
              pointer-events: none;
            `}
        `}
`;

const SidebarInner = styled.div<{
  $variant: PopoverSidebarContextType["state"];
}>`
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.backgroundColor.DEFAULT};
  width: ${POPOVER_SIDEBAR_WIDTH}px;

  ${({ $variant }) =>
    $variant === "docked"
      ? css`
          border-right: 1px solid ${({ theme }) => theme.borderColor.MUTED};
          height: 100%;
        `
      : css`
          box-shadow: ${({ theme }) => theme.boxShadow.POPOVER};
          border-radius: ${({ theme }) => theme.borderRadius};
          margin: ${FLOATING_SIDEBAR_TOP_SPACING}px 0
            ${FLOATING_SIDEBAR_BOTTOM_SPACING}px 0;
          max-height: calc(
            100% -
              ${FLOATING_SIDEBAR_TOP_SPACING +
              FLOATING_SIDEBAR_BOTTOM_SPACING}px
          );
        `};
`;

const SidebarStickySection = styled.div<{
  $hasTopBorder?: boolean;
}>`
  display: flex;
  flex-direction: column;

  border-top: ${({ $hasTopBorder, theme }) =>
    $hasTopBorder ? `1px solid ${theme.borderColor.MUTED}` : "none"};
`;

const SidebarScrollableSection = styled(ScrolledArea)`
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow-y: scroll;
  padding: 0 ${SIDEBAR_PADDING}px;
`;

const FullWidthChildFlex = styled(Flex)`
  > * {
    width: 100%;
  }
`;

const Spacer = styled.div`
  padding: ${SIDEBAR_SPACER}px;
`;

const StyledPopoverSidebarButton = styled(HexButton)`
  &&&&&&&&&& {
    cursor: default;
    padding-left: 22px;
    padding-right: 20px;
    margin-right: 0;
    height: 100%;
    border-radius: 0;
  }
`;

const NavigationItem = React.memo(function NavigationItem({
  counterEvent,
  icon,
  title,
  to,
}: {
  icon: React.ReactNode;
  title: React.ReactNode;
  to: string;
  counterEvent: ClientCounterEvent;
}) {
  const onClick = useCallback(() => {
    trackCounterEvent(counterEvent);
  }, [counterEvent]);
  return (
    <HexLinkButton
      css="justify-content: flex-start; align-items: center;"
      icon={icon}
      minimal={true}
      subtle={true}
      to={to}
      onClick={onClick}
    >
      {title}
    </HexLinkButton>
  );
});

type PopoverSidebarContextType = {
  state: PopoverSidebarState;
  toggleDocked: () => void;
  onHoverIn: () => void;
  onHoverOut: () => void;
  openNestedPopover: () => void;
  closeNestedPopover: () => void;
  toggleSidebarClickState: () => void;
};

const PopoverSidebarContext = React.createContext<PopoverSidebarContextType>({
  state: PopoverSidebarState.CLOSED,
  toggleDocked: () => {},
  onHoverIn: () => {},
  onHoverOut: () => {},
  openNestedPopover: () => {},
  closeNestedPopover: () => {},
  toggleSidebarClickState: () => {},
});

export const usePopoverSidebarContext = (): PopoverSidebarContextType => {
  return React.useContext(PopoverSidebarContext);
};

const useSetDebouncedHoverStates = (): {
  hovered: boolean;
  onHoverIn: () => void;
  onHoverOut: () => void;
} => {
  const [hovered, setHovered] = useState(false);
  const debouncedSetHover = useMemo(() => {
    let timer: NodeJS.Timeout | undefined = undefined;

    return (newHovered: boolean, delay: number) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        setHovered(newHovered);
      }, delay);
    };
  }, [setHovered]);

  const onHoverIn = useCallback(() => {
    debouncedSetHover(true, 300);
  }, [debouncedSetHover]);
  const onHoverOut = useCallback(() => {
    debouncedSetHover(false, 150);
  }, [debouncedSetHover]);
  return {
    hovered,
    onHoverIn,
    onHoverOut,
  };
};

export const PopoverSidebarProvider = React.memo(
  function PopoverSidebarProvider({ children }: { children: React.ReactNode }) {
    const [docked, setDocked] = useLocalStorage(
      LocalStorageKeys.POPOVER_SIDEBAR_DOCKED,
    );
    const [
      nestedPopoverOpen,
      __,
      { setFalse: closeNestedPopover, setTrue: openNestedPopover },
    ] = useToggleState(false);
    const enableSidebarDocking = useHexFlag("enable-sidebar-docking");
    const toggleDocked = useCallback(
      () => enableSidebarDocking && setDocked((prev) => !prev),
      [setDocked, enableSidebarDocking],
    );
    const [
      sidebarClickState,
      _unused,
      { setFalse: closeSidebarClickState, toggle: toggleSidebarClickState },
    ] = useToggleState(false);
    // Implement a way to autoclose the sidebar when clicking outside of the sidebar / button subtrees
    const handleClickToClose = useCallback(
      (evt: MouseEvent) => {
        if (!(evt.target instanceof Element)) {
          return;
        }
        if (
          evt.target.closest(dataTest(TestSelectors.POPOVER_SIDEBAR)) ||
          evt.target.closest(dataTest(TestSelectors.POPOVER_SIDEBAR_BUTTON))
        ) {
          return;
        }
        closeSidebarClickState();
      },
      [closeSidebarClickState],
    );
    useEffect(() => {
      document.addEventListener("click", handleClickToClose);

      return () => {
        document.removeEventListener("click", handleClickToClose);
      };
    }, [handleClickToClose]);

    const { hovered, onHoverIn, onHoverOut } = useSetDebouncedHoverStates();
    const state = useMemo(() => {
      if (enableSidebarDocking) {
        return docked
          ? PopoverSidebarState.DOCKED
          : hovered || nestedPopoverOpen
            ? PopoverSidebarState.HOVERED
            : PopoverSidebarState.CLOSED;
      } else {
        return sidebarClickState
          ? PopoverSidebarState.HOVERED
          : PopoverSidebarState.CLOSED;
      }
    }, [
      docked,
      hovered,
      enableSidebarDocking,
      nestedPopoverOpen,
      sidebarClickState,
    ]);

    const value = useMemo(
      () =>
        enableSidebarDocking
          ? {
              state,
              toggleDocked,
              onHoverIn,
              onHoverOut,
              openNestedPopover,
              closeNestedPopover,
              toggleSidebarClickState: noop,
            }
          : {
              state,
              toggleDocked: noop,
              onHoverIn: noop,
              onHoverOut: noop,
              openNestedPopover,
              closeNestedPopover,
              toggleSidebarClickState,
            },
      [
        toggleDocked,
        onHoverIn,
        onHoverOut,
        toggleSidebarClickState,
        enableSidebarDocking,
        state,
        openNestedPopover,
        closeNestedPopover,
      ],
    );

    return (
      <PopoverSidebarContext.Provider value={value}>
        {children}
      </PopoverSidebarContext.Provider>
    );
  },
);

const PopoverSidebarButton = React.memo(function PopoverSidebarButton() {
  const {
    onHoverIn,
    onHoverOut,
    state,
    toggleDocked,
    toggleSidebarClickState,
  } = usePopoverSidebarContext();
  const { data: unreadNotificationCountData } =
    useGetUnreadNotificationCountQuery();
  const hasUnreadNotifications =
    (unreadNotificationCountData?.me?.unreadNotificationCount ?? 0) > 0;

  const enableSidebarDocking = useHexFlag("enable-sidebar-docking");

  // If docked, we show the button elsewhere in the sidebar
  if (state === "docked") {
    return null;
  }
  if (enableSidebarDocking) {
    return (
      <Flex align="center" css="position: relative;">
        {hasUnreadNotifications && (
          <div css="position: absolute; right: 4px; top: 12px;">
            <UnreadIndicator isRead={false} />
          </div>
        )}
        <StyledPopoverSidebarButton
          data-test={TestSelectors.POPOVER_SIDEBAR_BUTTON}
          icon={<SidebarDockIcon />}
          minimal={true}
          subtle={true}
          onClick={toggleDocked}
          onMouseEnter={onHoverIn}
          onMouseLeave={onHoverOut}
        />
      </Flex>
    );
  } else {
    return (
      <Flex align="center" css="margin: 0 2px 0 10px; position: relative;">
        {hasUnreadNotifications && (
          <div css="position: absolute; right: 4px; top: 12px;">
            <UnreadIndicator isRead={false} />
          </div>
        )}
        <HexButton
          data-test={TestSelectors.POPOVER_SIDEBAR_BUTTON}
          icon={<MenuIcon />}
          minimal={true}
          onClick={toggleSidebarClickState}
        />
      </Flex>
    );
  }
});
export const PopoverButtonWithLogo = React.memo(function PopoverButtonWithLogo({
  appIconLoading,
  customAppIconUrl,
}: Pick<HexLogoProps, "appIconLoading" | "customAppIconUrl">) {
  const currentUser = useCurrentUser();
  return (
    <Flex align="stretch" css="height: 100%;">
      <PopoverSidebarButton />
      <HexTooltip
        content={`Go to ${currentUser?.org.displayName ?? "workspace"} home`}
        position="bottom"
      >
        <HexLogo
          appIconLoading={appIconLoading}
          customAppIconUrl={customAppIconUrl}
          logoPadding="small"
        />
      </HexTooltip>
    </Flex>
  );
});

export const PopoverSidebar = React.memo(function PopoverSidebar() {
  const { canViewThreadById } = useThreadsEnabledForUser();
  const {
    closeNestedPopover,
    onHoverIn,
    onHoverOut,
    openNestedPopover,
    state,
    toggleDocked,
  } = usePopoverSidebarContext();

  // 'Ask' uses the exploreFromScratch feature gate to determine if the user can create Explores.
  const newThreadsListPageEnabled = canViewThreadById;
  const currentUser = useCurrentUser();
  const showObservabilityTab = currentUser?.org?.canViewAgentStats ?? false;
  return state === "closed" ? null : (
    <PopoverSidebarContainer
      $variant={state}
      data-test={TestSelectors.POPOVER_SIDEBAR}
      onMouseEnter={onHoverIn}
      onMouseLeave={onHoverOut}
    >
      <CreateAssetDialogs />

      <SidebarInner $variant={state}>
        <SidebarStickySection>
          <Flex css="margin: 12px 20px 4px 8px;" justify="between">
            <div css="margin-left: 8px;">
              <WorkspaceDropdownPicker
                css="max-width: 220px;"
                hideSettings={true}
                location="popover_sidebar"
                showUserPreferences={true}
              />
            </div>
            <Flex gap={8}>
              <NotificationInboxButton
                onClosed={closeNestedPopover}
                onOpened={openNestedPopover}
              />
              {state === "docked" && (
                <HexButton icon={<SidebarDockIcon />} onClick={toggleDocked} />
              )}
            </Flex>
          </Flex>
          <FullWidthChildFlex direction="column">
            <div //Create new button
              css={`
                padding: 4px 20px 10px;
              `}
            >
              <QuickCreateButtons location="popover_sidebar" />
            </div>

            <div //Search bar
              css={`
                padding: 0 ${SIDEBAR_PADDING}px;
              `}
            >
              <WorkspaceSearchButton
                isNavItem={true}
                location="popover_sidebar"
                minimal={true}
                textOverride="Search"
              />
            </div>
            <div
              css={`
                padding: 0 ${SIDEBAR_PADDING}px 10px;
                width: 100%;

                > * {
                  width: 100%;
                }
              `}
            >
              <NavigationItem
                counterEvent="popover_sidebar.home.click"
                icon={<HomeIcon />}
                title="Home"
                to={Routes.HOME.getUrl({})}
              />
              {showObservabilityTab && (
                <NavigationItem
                  counterEvent="popover_sidebar.context_studio.click"
                  icon={<ContextStudioIcon />}
                  title="Context studio"
                  to={Routes.CONTEXT_STUDIO.getUrl({})}
                />
              )}
            </div>
          </FullWidthChildFlex>
        </SidebarStickySection>
        <SidebarScrollableSection>
          <FullWidthChildFlex direction="column">
            <NavigationItem
              counterEvent="popover_sidebar.collections.click"
              icon={<CollectionIcon />}
              title="Collections"
              to={Routes.HOME.getUrl({ subView: "collections" })}
            />
            <LibraryNavItem />
            <Spacer />
            {newThreadsListPageEnabled && (
              <NavigationItem
                counterEvent="popover_sidebar.threads.click"
                icon={<ThreadIcon />}
                title={
                  <Flex align="center" gap={6}>
                    Threads
                    <HexTag intent={Intent.PRIMARY} small={true}>
                      Beta
                    </HexTag>
                  </Flex>
                }
                to={Routes.HOME.getUrl({ subView: "threads" })}
              />
            )}
            <NavigationItem
              counterEvent="popover_sidebar.projects.click"
              icon={<ProjectIcon />}
              title="Projects"
              to={Routes.HOME.getUrl({ subView: "all" })}
            />
            <NavigationItem
              counterEvent="popover_sidebar.components.click"
              icon={<ComponentIcon />}
              title="Components"
              to={Routes.HOME.getUrl({ subView: "components" })}
            />
            <NavigationItem
              counterEvent="popover_sidebar.explore.click"
              icon={<ExploreIcon />}
              title="Explorations"
              to={Routes.HOME.getUrl({ subView: "explorations" })}
            />
            <NavigationItem
              counterEvent="popover_sidebar.data.click"
              icon={<DataSourcesIcon />}
              title="Data"
              to={Routes.HOME.getUrl({ subView: "data" })}
            />
            <Spacer />
          </FullWidthChildFlex>

          <ErrorBoundary>
            <Flex direction="column" gap={12}>
              <RecentItems hideIfEmpty={true} location="popover_sidebar" />
              <FavoriteProjects hideIfEmpty={true} location="popover_sidebar" />
              <FavoriteCollections
                hideIfEmpty={true}
                location="popover_sidebar"
              />
              <FavoriteData hideIfEmpty={true} location="popover_sidebar" />
            </Flex>
          </ErrorBoundary>
          <Spacer />
        </SidebarScrollableSection>
        <SidebarStickySection
          $hasTopBorder={true}
          css={`
            padding: 8px ${SIDEBAR_PADDING}px;
          `}
        >
          <NavigationItem
            counterEvent="popover_sidebar.settings.click"
            icon={<ConfigureIcon />}
            title="Settings"
            to={Routes.SETTINGS.getUrl({})}
          />
        </SidebarStickySection>
      </SidebarInner>
    </PopoverSidebarContainer>
  );
});

const LibraryNavItem = () => {
  const hasLibraryStatuses = useHasLibraryStatuses();
  return hasLibraryStatuses ? (
    <NavigationItem
      counterEvent="popover_sidebar.library.click"
      icon={<LibraryIcon />}
      title="Library"
      to={Routes.HOME.getUrl({ subView: "library" })}
    />
  ) : null;
};
